import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getCustomers, 
  getProducts, 
  getBatches, 
  createDispatch, 
  updateDispatch, 
  getDispatch,
  lockDispatch,
  openDispatchPdf,
  openPalletLabels,
  mintDownloadToken,
  getCustomerAddresses,
  printLabel,
  getDispatchEditHistory,
  getDispatchEmailTemplate,
  sendDispatchEmail,
  getCoBrandings,
  getTransportOptions,
  getOperators,
  predictUseByDates,
  getCustomerMappingsLookup,
} from '../lib/api';
import { getTodayFormatted, formatNumber } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Save, 
  Lock, 
  FileText, 
  Plus, 
  Trash2, 
  Loader2,
  ArrowLeft,
  Printer,
  Tag,
  Edit,
  History,
  AlertTriangle,
  Mail,
  Send,
  Sparkles,
  Package
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { DatePicker } from '../components/ui/date-picker';
import AldiBranchDialog from '../components/AldiBranchDialog';

export default function DispatchBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aldiBranchOpen, setAldiBranchOpen] = useState(false);
  const [aldiBranchDispatchId, setAldiBranchDispatchId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [cobrandings, setCobrandings] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  
  // Edit locked dispatch state
  const [editLockedDialogOpen, setEditLockedDialogOpen] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [isEditingLocked, setIsEditingLocked] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Email state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    to_email: '',
    cc_emails: [],
    subject: '',
    body: '',
    attach_pdf: true
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [ccInput, setCcInput] = useState('');
  
  // Use-by date warning state
  const [useByWarningOpen, setUseByWarningOpen] = useState(false);
  const [expiredBatches, setExpiredBatches] = useState([]);
  
  // Batch code input refs for autofocus
  const batchCodeRefs = useRef({});

  // Operator (prepared by)
  const [operators, setOperators] = useState([]);
  const [preparedByOperatorCode, setPreparedByOperatorCode] = useState('');
  const [preparedByOperatorName, setPreparedByOperatorName] = useState('');
  
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('primary');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [dateOrderReceived, setDateOrderReceived] = useState('');
  const [dispatchDate, setDispatchDate] = useState(getTodayFormatted());
  const [customerDeliveryDate, setCustomerDeliveryDate] = useState('');
  const [transportAgent, setTransportAgent] = useState('');
  const [numPallets, setNumPallets] = useState('');
  const [packagingWeight, setPackagingWeight] = useState(''); // Manual packaging weight override
  const [notes, setNotes] = useState('');
  const [dispatchNumber, setDispatchNumber] = useState('');
  const [status, setStatus] = useState('draft');
  const [orderedQuantities, setOrderedQuantities] = useState({}); // Track original ordered quantities
  
  // Lines
  const [lines, setLines] = useState([createEmptyLine()]);

  // Use-By auto-suggest predictions keyed by `${supplier_product_code}|${product_id}`
  const [useByPredictions, setUseByPredictions] = useState({});
  const autoFilledUseByRef = useRef(new Set());
  // Customer product code mappings lookup: productId or `productId:cobrandingId` -> { supplier_product_code }
  const [customerMappings, setCustomerMappings] = useState({});
  
  // Totals
  const [totals, setTotals] = useState({ cases: 0, netWeight: 0, grossWeight: 0 });
  const [orderedTotals, setOrderedTotals] = useState({ units: 0, weight: 0, cases: 0 }); // Reference: ordered amounts

  // Check if user has permission to edit locked dispatches
  const canEditLockedDispatch = useMemo(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role === 'super_admin' || (user.permissions || []).includes('edit_locked_dispatch');
  }, []);

  function createEmptyLine() {
    return {
      id: (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)),
      product_id: '',
      batch_id: '',
      quantity: '',
      unit_type: 'units',
      notes: '',
      // Display fields
      product_name: '',
      cobranding_id: '',
      cobranding_name: '',
      batch_code: '',
      use_by_date: '',
      no_use_by_date: false,
      ordered_quantity: '', // Original quantity from order
      supplier_product_code: '', // Customer-specific product code
      // Manual entry fields (packaging staff can override)
      cases_override: '',
      weight_override: '',
    };
  }

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load dispatch only after initial data is loaded (products, customers, etc.)
  useEffect(() => {
    if (isEdit && !loading && products.length > 0) {
      loadDispatch();
    }
  }, [id, loading, products.length]);

  // Calculate ordered totals for reference (not linked to operator inputs)
  useEffect(() => {
    if (lines.length > 0 && products.length > 0) {
      let totalUnits = 0;
      let totalWeight = 0;
      let totalCases = 0;
      
      lines.forEach(line => {
        const qty = parseFloat(line.ordered_quantity || line.quantity) || 0;
        totalUnits += qty;
        
        // Weight: Use saved weight (estimated_weight, total_weight) or calculate from product
        const lineWeight = parseFloat(line.estimated_weight || line.total_weight) || 0;
        if (lineWeight > 0) {
          totalWeight += lineWeight;
        } else {
          const product = products.find(p => p.id === line.product_id);
          if (product && product.weight_per_unit) {
            totalWeight += qty * product.weight_per_unit;
          }
        }
        
        // Cases: Use saved cases or calculate from product
        const lineCases = parseFloat(line.cases || line.calculated_cases) || 0;
        if (lineCases > 0) {
          totalCases += lineCases;
        } else {
          const product = products.find(p => p.id === line.product_id);
          if (product && product.units_per_case) {
            totalCases += qty / product.units_per_case;
          }
        }
      });
      
      setOrderedTotals({
        units: Math.round(totalUnits * 100) / 100,
        weight: Math.round(totalWeight * 100) / 100,
        cases: Math.round(totalCases * 10) / 10
      });
    }
  }, [lines, products]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load each data source independently to prevent one failure from blocking all
      const [customersRes, productsRes, batchesRes, cobrandingsRes, transportRes, operatorsRes] = await Promise.allSettled([
        getCustomers({ is_active: true }),
        getProducts({ is_active: true }),
        getBatches({ available_only: true }),
        getCoBrandings({ is_active: true }),
        getTransportOptions(),
        getOperators(),
      ]);
      
      // Set data from successful responses - default to empty arrays
      setCustomers(customersRes.status === 'fulfilled' ? (customersRes.value.data || []) : []);
      setProducts(productsRes.status === 'fulfilled' ? (productsRes.value.data || []) : []);
      setAllBatches(batchesRes.status === 'fulfilled' ? (batchesRes.value.data || []) : []);
      setCobrandings(cobrandingsRes.status === 'fulfilled' ? (cobrandingsRes.value.data || []) : []);
      setTransportOptions(transportRes.status === 'fulfilled' ? (transportRes.value.data || []) : []);
      setOperators(operatorsRes.status === 'fulfilled' ? (operatorsRes.value.data || []) : []);
      
      // Log any failures for debugging
      const failures = [];
      if (customersRes.status === 'rejected') failures.push('customers');
      if (productsRes.status === 'rejected') failures.push('products');
      if (batchesRes.status === 'rejected') failures.push('batches');
      
      if (failures.length > 0) {
        console.error('Failed to load:', failures.join(', '));
        // Only show error toast if critical data actually failed
        if ((customersRes.status === 'rejected' || productsRes.status === 'rejected')) {
          const errorReason = customersRes.reason || productsRes.reason;
          if (errorReason?.response?.status === 401) {
            toast.error('Session expired. Please log in again.');
          } else {
            toast.error('Some data failed to load. Please refresh the page.');
          }
        }
      }
    } catch (error) {
      console.error('loadInitialData error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDispatch = async () => {
    try {
      const response = await getDispatch(id);
      const dispatch = response.data;
      
      setCustomerId(dispatch.customer_id);
      setPoNumber(dispatch.po_number || '');
      setPoDate(dispatch.po_date || '');
      setDateOrderReceived(dispatch.date_order_received || '');
      setDispatchDate(dispatch.dispatch_date || '');
      setCustomerDeliveryDate(dispatch.customer_delivery_date || '');
      setTransportAgent(dispatch.vehicle_info || '');
      setNumPallets(dispatch.num_pallets?.toString() || '');
      setPackagingWeight(dispatch.packaging_weight?.toString() || '');
      setNotes(dispatch.notes || '');
      setDispatchNumber(dispatch.dispatch_number);
      setStatus(dispatch.status);
      setPreparedByOperatorCode(dispatch.prepared_by_operator_code || '');
      setPreparedByOperatorName(dispatch.prepared_by_operator_name || '');
      
      // Load customer
      const customer = customers.find(c => c.id === dispatch.customer_id);
      setCustomerData(customer);
      
      // Load lines with cobranding info
      if (dispatch.lines && dispatch.lines.length > 0) {
        const linesWithCobranding = dispatch.lines.map(line => {
          const cobranding = cobrandings.find(c => c.id === line.cobranding_id);
          const product = products.find(p => p.id === line.product_id);
          return {
            ...line,
            id: line.id || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)),
            quantity: line.quantity?.toString() || '',
            product_name: line.product_name || product?.name || '',  // Ensure product_name is set
            cobranding_name: cobranding?.name || 'Standard',
            ordered_quantity: line.quantity?.toString() || '', // Store original ordered qty
            // Preserve batch code and use-by date from saved dispatch
            batch_code: line.batch_code || '',
            use_by_date: line.use_by_date || '',
            no_use_by_date: line.use_by_date === 'No Date' || line.no_use_by_date || false,
            batch_id: line.batch_id || '',
            // Preserve supplier product code
            supplier_product_code: line.supplier_product_code || '',
            // Map saved values to override fields for display in the form
            weight_override: line.total_weight > 0 ? line.total_weight.toString() : '',
            cases_override: (line.cases || line.calculated_cases) > 0 ? (line.cases || line.calculated_cases).toString() : '',
            // Also preserve estimated_weight for display
            estimated_weight: line.estimated_weight || line.total_weight || 0,
          };
        });
        setLines(linesWithCobranding);
        
        // Build ordered quantities map by product
        const orderedQtys = {};
        linesWithCobranding.forEach(line => {
          if (line.product_id) {
            orderedQtys[line.product_id] = (orderedQtys[line.product_id] || 0) + parseFloat(line.quantity || 0);
          }
        });
        setOrderedQuantities(orderedQtys);
      }
      
      setTotals({
        cases: dispatch.total_cases || 0,
        netWeight: dispatch.total_net_weight || 0,
        grossWeight: dispatch.total_gross_weight || 0,
      });
    } catch (error) {
      toast.error('Failed to load dispatch');
      navigate('/dispatches');
    }
  };

  // Update customer data and load addresses when selection changes
  useEffect(() => {
    if (customerId) {
      const customer = customers.find(c => c.id === customerId);
      setCustomerData(customer);
      
      // Load customer addresses
      getCustomerAddresses(customerId)
        .then(response => {
          setCustomerAddresses(response.data.all_addresses || []);
          // Select default address
          const defaultAddr = response.data.all_addresses?.find(a => a.is_default);
          setSelectedAddressId(defaultAddr?.id || 'primary');
        })
        .catch(() => {
          setCustomerAddresses([]);
          setSelectedAddressId('primary');
        });
    } else {
      setCustomerData(null);
      setCustomerAddresses([]);
      setSelectedAddressId('primary');
    }
  }, [customerId, customers]);

  // Get selected delivery address
  const selectedAddress = useMemo(() => {
    return customerAddresses.find(a => a.id === selectedAddressId) || customerAddresses[0] || null;
  }, [customerAddresses, selectedAddressId]);

  // Get batches for a specific product (only with available quantity)
  const getBatchesForProduct = (productId) => {
    return allBatches.filter(b => b.product_id === productId && (b.quantity_available || 0) > 0);
  };

  // Get max available quantity for a line (based on selected batch)
  const getMaxQuantityForLine = (line) => {
    if (line.batch_id) {
      const batch = allBatches.find(b => b.id === line.batch_id);
      return batch ? (batch.quantity_available || 0) : null;
    }
    return null; // No limit for manual batch entry
  };

  // Calculate suggested values based on product settings (for hints only)
  const getSuggestedValues = (line, productsList) => {
    const product = productsList.find(p => p.id === line.product_id);
    if (!product || !line.quantity) {
      return { suggested_cases: 0, suggested_weight: 0 };
    }
    
    const quantity = parseFloat(line.quantity) || 0;
    const unitsPerCase = product.units_per_case || 1;
    const weightPerUnit = product.weight_per_unit || 0;
    
    return {
      suggested_cases: Math.round((quantity / unitsPerCase) * 100) / 100,
      suggested_weight: Math.round((quantity * weightPerUnit) * 100) / 100
    };
  };

  // Get actual line values (manual override takes priority)
  const getLineValues = (line, productsList) => {
    const suggested = getSuggestedValues(line, productsList);
    
    // Use manual override if provided, otherwise use suggested
    const cases = line.cases_override !== '' && line.cases_override !== undefined 
      ? parseFloat(line.cases_override) || 0 
      : suggested.suggested_cases;
    const weight = line.weight_override !== '' && line.weight_override !== undefined 
      ? parseFloat(line.weight_override) || 0 
      : suggested.suggested_weight;
    
    return {
      cases: Math.round(cases * 100) / 100,
      weight: Math.round(weight * 100) / 100,
      is_cases_overridden: line.cases_override !== '' && line.cases_override !== undefined,
      is_weight_overridden: line.weight_override !== '' && line.weight_override !== undefined
    };
  };

  // Handle line changes
  const updateLine = (index, field, value) => {
    // Prevent negative values for numeric fields
    if (['quantity', 'cases_override', 'weight_override'].includes(field)) {
      const numValue = parseFloat(value);
      if (numValue < 0) {
        return; // Don't allow negative values
      }
    }
    
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-populate when product changes
      if (field === 'product_id' && value) {
        const product = products.find(p => p.id === value);
        if (product) {
          updated[index].product_name = product.name;
          updated[index].batch_id = '';
          updated[index].batch_code = '';
          updated[index].use_by_date = '';
          // Clear overrides when product changes so suggested values show
          updated[index].cases_override = '';
          updated[index].weight_override = '';
        }
        // Auto-focus the batch code input after product selection
        // Use requestAnimationFrame + setTimeout to ensure DOM has updated
        requestAnimationFrame(() => {
          setTimeout(() => {
            const inputEl = batchCodeRefs.current[index];
            if (inputEl) {
              inputEl.focus();
            }
          }, 150);
        });
      }

      // Resolve supplier product code from customer mappings when product/branding changes
      if (field === 'product_id' || field === 'cobranding_id') {
        const pid = updated[index].product_id;
        const cid = updated[index].cobranding_id;
        if (pid) {
          const m = (cid && customerMappings[`${pid}:${cid}`]) || customerMappings[pid];
          updated[index].supplier_product_code = m?.supplier_product_code || '';
        }
      }
      
      // Auto-populate when batch changes
      if (field === 'batch_id' && value) {
        const batch = allBatches.find(b => b.id === value);
        if (batch) {
          updated[index].batch_code = batch.batch_code;
          updated[index].use_by_date = batch.use_by_date;
          updated[index].no_use_by_date = batch.use_by_date === 'No Date';
        }
      }

      // Handle no_use_by_date toggle
      if (field === 'no_use_by_date') {
        if (value) {
          updated[index].use_by_date = 'No Date';
        } else {
          updated[index].use_by_date = '';
        }
      }
      
      // When quantity changes, clear overrides to show new suggested values
      // (user can re-enter manual values if needed)
      if (field === 'quantity') {
        // Don't clear overrides - let user keep their manual entries
      }
      
      return updated;
    });
  };

  // Calculate totals whenever lines change (using actual values including overrides)
  const calculatedTotals = useMemo(() => {
    let totalCases = 0;
    let totalNetWeight = 0;
    
    // Group by product
    const byProduct = {};
    
    lines.forEach(line => {
      // Skip out-of-stock lines from totals
      if (line.out_of_stock) return;
      
      const values = getLineValues(line, products);
      totalCases += values.cases;
      totalNetWeight += values.weight;
      
      // Group by product - use product_name from line if available
      if (line.product_id) {
        const product = products.find(p => p.id === line.product_id);
        const productName = line.product_name || product?.name || 'Unknown Product';
        
        if (!byProduct[line.product_id]) {
          byProduct[line.product_id] = {
            product_id: line.product_id,
            product_name: productName,
            lines: 0,
            cases: 0,
            netWeight: 0,
            grossWeight: 0
          };
        }
        
        byProduct[line.product_id].lines += 1;
        byProduct[line.product_id].cases += values.cases;
        byProduct[line.product_id].netWeight += values.weight;
        byProduct[line.product_id].grossWeight += values.weight * 1.02;
      }
    });
    
    // Convert to array and round values
    const productTotals = Object.values(byProduct).map(p => ({
      ...p,
      cases: Math.round(p.cases * 100) / 100,
      netWeight: Math.round(p.netWeight * 100) / 100,
      grossWeight: Math.round(p.grossWeight * 100) / 100
    }));
    
    return {
      cases: Math.round(totalCases * 100) / 100,
      netWeight: Math.round(totalNetWeight * 100) / 100,
      grossWeight: Math.round(totalNetWeight * 1.02 * 100) / 100, // 2% packaging
      byProduct: productTotals
    };
  }, [lines, products]);

  // ── Use-By date auto-suggest (learned per Customer Product Code) ──
  useEffect(() => {
    if (!customerId) { setCustomerMappings({}); return; }
    let cancelled = false;
    getCustomerMappingsLookup(customerId)
      .then(({ data }) => { if (!cancelled) setCustomerMappings(data.lookup || {}); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [customerId]);

  const predictionSignature = useMemo(
    () => JSON.stringify(lines.map(l => `${l.supplier_product_code || ''}|${l.product_id || ''}`)),
    [lines]
  );

  useEffect(() => {
    if (!customerId) return;
    const reqLines = lines
      .filter(l => l.supplier_product_code || l.product_id)
      .map(l => ({ supplier_product_code: l.supplier_product_code || null, product_id: l.product_id || null }));
    if (reqLines.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await predictUseByDates({ customer_id: customerId, dispatch_date: dispatchDate || '', lines: reqLines });
        if (cancelled) return;
        const preds = {};
        (data.predictions || []).forEach(p => {
          if (p.found && p.predicted_date) preds[`${p.supplier_product_code || ''}|${p.product_id || ''}`] = p;
        });
        setUseByPredictions(preds);
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, dispatchDate, predictionSignature]);

  const getLinePrediction = (line) => useByPredictions[`${line.supplier_product_code || ''}|${line.product_id || ''}`];

  const getUseByPredictionTitle = (p) => {
    if (!p) return '';
    if (p.source === 'code_history') return `Auto-suggested: learned from ${p.sample_count} past order${p.sample_count === 1 ? '' : 's'} for code ${p.supplier_product_code} (${p.shelf_life_days} days shelf life). Fully editable.`;
    if (p.source === 'manual') return `Auto-suggested: manually set shelf life for code ${p.supplier_product_code} (${p.shelf_life_days} days). Fully editable.`;
    return `Auto-suggested from product default shelf life (${p.shelf_life_days} days). Fully editable.`;
  };

  // Auto-fill empty Use-By dates with the prediction — once per line+code so a
  // deliberately cleared date is not re-filled. Draft/in-progress orders only.
  // NOTE: ref bookkeeping stays OUTSIDE the setLines updater (StrictMode-safe).
  useEffect(() => {
    if (Object.keys(useByPredictions).length === 0) return;
    if (status !== 'draft' && status !== 'in_progress') return;
    const patches = {};
    lines.forEach((l) => {
      if (l.use_by_date || l.no_use_by_date || l.batch_id) return;
      const predKey = `${l.supplier_product_code || ''}|${l.product_id || ''}`;
      const p = useByPredictions[predKey];
      if (!p) return;
      const appliedKey = `${l.id}|${predKey}`;
      if (autoFilledUseByRef.current.has(appliedKey)) return;
      autoFilledUseByRef.current.add(appliedKey);
      patches[l.id] = p.predicted_date;
    });
    if (Object.keys(patches).length === 0) return;
    setLines(prev => prev.map(l => (patches[l.id] && !l.use_by_date ? { ...l, use_by_date: patches[l.id] } : l)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useByPredictions, status, lines]);

  const addLine = () => {
    setLines(prev => [...prev, createEmptyLine()]);
  };

  const removeLine = (index) => {
    if (lines.length > 1) {
      setLines(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e, rowIndex, field) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Move to next row
      if (rowIndex === lines.length - 1) {
        addLine();
      }
      // If entering from batch code, focus next row's batch code (if product set) or product
      if (field === 'batch_code') {
        setTimeout(() => {
          const nextBatch = batchCodeRefs.current[rowIndex + 1];
          if (nextBatch) {
            nextBatch.focus();
          } else {
            const nextInput = document.querySelector(`[data-row="${rowIndex + 1}"][data-field="product"]`);
            nextInput?.focus();
          }
        }, 100);
      } else {
        // Focus next row's first input
        setTimeout(() => {
          const nextInput = document.querySelector(`[data-row="${rowIndex + 1}"][data-field="product"]`);
          nextInput?.focus();
        }, 50);
      }
    } else if (e.key === 'Tab' && !e.shiftKey && field === 'notes' && rowIndex === lines.length - 1) {
      e.preventDefault();
      addLine();
      setTimeout(() => {
        const nextInput = document.querySelector(`[data-row="${rowIndex + 1}"][data-field="product"]`);
        nextInput?.focus();
      }, 50);
    }
  };

  // Save dispatch with comprehensive validation
  const handleSave = async () => {
    const warnings = [];
    const errors = [];
    
    if (!customerId) {
      toast.error('Please select a customer before saving');
      return;
    }

    // Dispatch date is only required for non-draft statuses
    if (!dispatchDate && status !== 'pending' && status !== 'draft') {
      toast.error('Please select a dispatch date');
      return;
    }
    
    // Warn about missing dispatch date for pending/draft
    if (!dispatchDate) {
      warnings.push('Dispatch date not set - order will remain in draft/pending');
    }

    // Validate lines
    const validLines = [];
    
    lines.forEach((line, index) => {
      if (!line.product_id) {
        if (line.quantity || line.batch_code || line.notes) {
          errors.push(`Line ${index + 1}: Please select a product`);
        }
        return; // Skip empty lines
      }
      
      if (!line.quantity || parseFloat(line.quantity) <= 0) {
        errors.push(`Line ${index + 1} (${line.product_name || 'Unknown'}): Quantity must be greater than 0`);
        return;
      }
      
      // Warn about missing batch (but don't block for drafts)
      if (!line.batch_code && !line.batch_id) {
        warnings.push(`Line ${index + 1} (${line.product_name}): No batch specified`);
      }
      
      validLines.push(line);
    });
    
    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold mb-2">Please fix the following:</p>
          <ul className="list-disc list-inside text-sm">
            {errors.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>,
        { duration: 6000 }
      );
      return;
    }

    if (validLines.length === 0) {
      toast.error('Please add at least one product with quantity');
      return;
    }
    
    // Show warnings but proceed
    if (warnings.length > 0) {
      toast.warning(
        <div>
          <p className="font-semibold mb-1">Missing optional fields:</p>
          <ul className="list-disc list-inside text-sm">
            {warnings.slice(0, 3).map((msg, i) => <li key={i}>{msg}</li>)}
            {warnings.length > 3 && <li>...and {warnings.length - 3} more</li>}
          </ul>
        </div>,
        { duration: 4000 }
      );
    }

    setSaving(true);
    try {
      const data = {
        customer_id: customerId,
        po_number: poNumber,
        po_date: poDate,
        dispatch_date: dispatchDate,
        customer_delivery_date: customerDeliveryDate || null,
        vehicle_info: transportAgent || null,
        num_pallets: numPallets ? parseInt(numPallets) : null,
        packaging_weight: packagingWeight ? parseFloat(packagingWeight) : null,
        notes,
        prepared_by_operator_code: preparedByOperatorCode || null,
        prepared_by_operator_name: preparedByOperatorName || null,
        lines: validLines.map(l => ({
          product_id: l.product_id,
          product_name: l.product_name || products.find(p => p.id === l.product_id)?.name || '',
          batch_id: l.batch_id || null,
          batch_code: l.batch_code || null,
          use_by_date: l.no_use_by_date ? 'No Date' : (l.use_by_date || null),
          cobranding_id: l.cobranding_id || null,
          quantity: parseFloat(l.quantity),
          unit_type: l.unit_type,
          notes: l.notes,
          supplier_product_code: l.supplier_product_code || null,  // Customer-specific product code
          // Send weight/cases overrides if manually entered
          weight_override: l.weight_override ? parseFloat(l.weight_override) : null,
          cases_override: l.cases_override ? parseFloat(l.cases_override) : null,
        })),
      };

      if (isEdit) {
        const response = await updateDispatch(id, data);
        setTotals({
          cases: response.data.total_cases || 0,
          netWeight: response.data.total_net_weight || 0,
          grossWeight: response.data.total_gross_weight || 0,
        });
        toast.success('Dispatch updated');
      } else {
        const response = await createDispatch(data);
        toast.success('Dispatch created');
        navigate(`/dispatch/${response.data.id}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const messages = detail.map(e => e.msg || 'Validation error');
        toast.error(
          <div>
            <p className="font-semibold mb-2">Validation errors:</p>
            <ul className="list-disc list-inside text-sm">
              {messages.slice(0, 3).map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error('Failed to save dispatch. Please check all fields and try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Lock dispatch
  // Check for expired use-by dates
  const checkExpiredBatches = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const expired = lines.filter(line => {
      if (!line.use_by_date || line.no_use_by_date || line.use_by_date === 'No Date') return false;
      try {
        // Parse DD/MM/YYYY format
        const parts = line.use_by_date.split('/');
        if (parts.length !== 3) return false;
        const useByDate = new Date(parts[2], parts[1] - 1, parts[0]);
        return useByDate < today;
      } catch {
        return false;
      }
    }).map(line => ({
      product_name: line.product_name || products.find(p => p.id === line.product_id)?.name || 'Unknown Product',
      batch_code: line.batch_code,
      use_by_date: line.use_by_date
    }));
    
    return expired;
  };

  const handleLock = async () => {
    if (!id) return;
    
    // Check for expired use-by dates
    const expired = checkExpiredBatches();
    if (expired.length > 0) {
      setExpiredBatches(expired);
      setUseByWarningOpen(true);
      return;
    }
    
    await proceedWithLock();
  };
  
  const proceedWithLock = async () => {
    if (!id) return;
    setUseByWarningOpen(false);
    
    try {
      await lockDispatch(id);
      setStatus('locked');
      toast.success('Dispatch locked and ready for printing');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || 'Error').join(', '));
      } else {
        toast.error(detail || 'Failed to lock dispatch');
      }
    }
  };

  // View PDF
  const handleViewPDF = async () => {
    if (!id) return;
    await openDispatchPdf(id);
  };

  // Print label for a single line
  const handlePrintLabel = async (lineIndex) => {
    const line = lines[lineIndex];
    if (!line || !id) return;
    
    try {
      const lineId = line.line_id || line.id;
      await printLabel(id, lineId);
      toast.success(`Label printed for ${line.product_name || 'item'}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to print label');
    }
  };

  // Print all labels
  const handlePrintAllLabels = async () => {
    if (!id || lines.length === 0) return;
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const line of lines) {
      if (!line.product_id) continue;
      try {
        const lineId = line.line_id || line.id;
        await printLabel(id, lineId);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    if (errorCount === 0) {
      toast.success(`${successCount} label(s) printed`);
    } else {
      toast.warning(`${successCount} printed, ${errorCount} failed`);
    }
  };

  // Print dispatch document directly to printer
  const handlePrintDocument = async () => {
    if (!id) return;

    // Mint a single-use download token then open + auto-print. Same DLT token
    // is reused for the window so the browser can navigate to it once.
    try {
      const token = await mintDownloadToken('dispatch_pdf_print', id);
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/dispatches/${id}/download-pdf?token=${encodeURIComponent(token)}`;
      const printWindow = window.open(pdfUrl, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        toast.success('Print dialog opened');
      } else {
        toast.error('Could not open print dialog. Please allow pop-ups.');
      }
    } catch (e) {
      toast.error('Failed to prepare printable PDF');
    }
  };

  // Print pallet label - navigate to Pallet Label Designer
  const handlePrintPalletLabel = () => {
    if (!id) return;
    navigate(`/pallet-labels/${id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Read-only unless draft OR editing locked dispatch
  const isReadOnly = status === 'locked' && !isEditingLocked;

  // Handle initiating edit of locked dispatch
  const handleEditLockedClick = () => {
    setEditReason('');
    setEditLockedDialogOpen(true);
  };

  // Confirm editing locked dispatch
  const confirmEditLocked = () => {
    if (!editReason || editReason.trim().length < 3) {
      toast.error('Please provide a reason for editing this locked dispatch');
      return;
    }
    setIsEditingLocked(true);
    setEditLockedDialogOpen(false);
    toast.info('You can now edit this dispatch. Changes will be tracked.');
  };

  // Handle saving when editing locked dispatch
  const handleSaveLockedEdit = async () => {
    if (!editReason || editReason.trim().length < 3) {
      toast.error('Please provide a reason for the changes');
      return;
    }
    
    setSaving(true);
    try {
      // Build lines payload
      const payload = {
        customer_id: customerId,
        po_number: poNumber,
        po_date: poDate,
        dispatch_date: dispatchDate,
        customer_delivery_date: customerDeliveryDate || null,
        vehicle_info: transportAgent || null,
        num_pallets: numPallets ? parseInt(numPallets) : null,
        notes: notes,
        prepared_by_operator_code: preparedByOperatorCode || null,
        prepared_by_operator_name: preparedByOperatorName || null,
        edit_reason: editReason,
        lines: lines.filter(l => l.product_id).map(l => ({
          product_id: l.product_id,
          product_name: l.product_name || products.find(p => p.id === l.product_id)?.name || '',
          quantity: parseFloat(l.quantity) || 0,
          unit_type: l.unit_type || 'units',
          cobranding_id: l.cobranding_id || null,
          notes: l.notes,
          batch_id: l.batch_id,
          batch_code: l.batch_code,
          use_by_date: l.use_by_date,
          supplier_product_code: l.supplier_product_code || null,
          cases_override: l.cases_override ? parseFloat(l.cases_override) : undefined,
          weight_override: l.weight_override ? parseFloat(l.weight_override) : undefined,
        }))
      };

      await updateDispatch(id, payload);
      toast.success('Dispatch updated successfully');
      setIsEditingLocked(false);
      setEditReason('');
      
      // Reload the dispatch data
      const response = await getDispatch(id);
      const data = response.data;
      setStatus(data.status);
      // Process lines through same mapping as loadDispatch
      if (data.lines && data.lines.length > 0) {
        const linesWithCobranding = data.lines.map(line => {
          const cobranding = cobrandings.find(c => c.id === line.cobranding_id);
          const product = products.find(p => p.id === line.product_id);
          return {
            ...line,
            id: line.id || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)),
            quantity: line.quantity?.toString() || '',
            product_name: line.product_name || product?.name || '',
            cobranding_name: cobranding?.name || 'Standard',
            ordered_quantity: line.quantity?.toString() || '',
            batch_code: line.batch_code || '',
            use_by_date: line.use_by_date || '',
            no_use_by_date: line.use_by_date === 'No Date' || line.no_use_by_date || false,
            batch_id: line.batch_id || '',
            supplier_product_code: line.supplier_product_code || '',
            weight_override: line.total_weight > 0 ? line.total_weight.toString() : '',
            cases_override: (line.cases || line.calculated_cases) > 0 ? (line.cases || line.calculated_cases).toString() : '',
            estimated_weight: line.estimated_weight || line.total_weight || 0,
          };
        });
        setLines(linesWithCobranding);
      } else {
        setLines([]);
      }
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing locked dispatch
  const cancelEditLocked = () => {
    setIsEditingLocked(false);
    setEditReason('');
    // Reload original data
    loadDispatchData();
    toast.info('Changes discarded');
  };

  // Load edit history
  const loadEditHistory = async () => {
    if (!id) return;
    try {
      const response = await getDispatchEditHistory(id);
      setEditHistory(response.data.edit_history || []);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load edit history');
    }
  };

  // Load dispatch data for reload
  const loadDispatchData = async () => {
    if (!id) return;
    try {
      const response = await getDispatch(id);
      const data = response.data;
      setCustomerId(data.customer_id || '');
      setPoNumber(data.po_number || '');
      setPoDate(data.po_date || '');
      setDateOrderReceived(data.date_order_received || '');
      setDispatchDate(data.dispatch_date || getTodayFormatted());
      setCustomerDeliveryDate(data.customer_delivery_date || '');
      setTransportAgent(data.vehicle_info || '');
      setNumPallets(data.num_pallets?.toString() || '');
      setNotes(data.notes || '');
      setDispatchNumber(data.dispatch_number || '');
      setStatus(data.status || 'draft');
      
      // Load lines with cobranding
      if (data.lines?.length > 0) {
        const linesWithCobranding = data.lines.map(line => {
          const cobranding = cobrandings.find(c => c.id === line.cobranding_id);
          return {
            ...line,
            cobranding_name: cobranding?.name || 'Standard',
            ordered_quantity: line.quantity?.toString() || '',
            // Preserve batch code and use-by date from saved dispatch
            batch_code: line.batch_code || '',
            use_by_date: line.use_by_date || '',
            no_use_by_date: line.use_by_date === 'No Date' || line.no_use_by_date || false,
            batch_id: line.batch_id || '',
            // Map saved values to override fields for display in the form
            weight_override: line.total_weight > 0 ? line.total_weight.toString() : '',
            cases_override: (line.cases || line.calculated_cases) > 0 ? (line.cases || line.calculated_cases).toString() : '',
            // Also preserve estimated_weight for display
            estimated_weight: line.estimated_weight || line.total_weight || 0,
          };
        });
        setLines(linesWithCobranding);
      } else {
        setLines([createEmptyLine()]);
      }
    } catch (error) {
      console.error('Failed to load dispatch:', error);
    }
  };

  // Open email dialog with pre-filled template
  const handleOpenEmailDialog = async () => {
    if (!id) return;
    try {
      const response = await getDispatchEmailTemplate(id);
      const template = response.data;
      setEmailData({
        to_email: template.to_email || '',
        cc_emails: [],
        subject: template.subject || '',
        body: template.body || '',
        attach_pdf: true
      });
      setCcInput('');
      setEmailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load email template');
    }
  };

  // Add CC email
  const handleAddCc = () => {
    const email = ccInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!emailData.cc_emails.includes(email)) {
        setEmailData(prev => ({
          ...prev,
          cc_emails: [...prev.cc_emails, email]
        }));
      }
      setCcInput('');
    } else if (email) {
      toast.error('Please enter a valid email address');
    }
  };

  // Remove CC email
  const handleRemoveCc = (email) => {
    setEmailData(prev => ({
      ...prev,
      cc_emails: prev.cc_emails.filter(e => e !== email)
    }));
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailData.to_email) {
      toast.error('Please enter recipient email');
      return;
    }
    if (!emailData.subject) {
      toast.error('Please enter a subject');
      return;
    }
    
    setSendingEmail(true);
    try {
      await sendDispatchEmail(id, emailData);
      toast.success('Email sent successfully!');
      setEmailDialogOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dispatch-builder">
      {/* Editing Locked Warning Banner */}
      {isEditingLocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-amber-800">Editing Locked Dispatch</h4>
            <p className="text-sm text-amber-700 mt-1">
              You are editing a locked dispatch. All changes will be tracked with your reason.
            </p>
            <div className="mt-2 space-y-2">
              <Label className="text-amber-800">Reason for changes *</Label>
              <Textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Explain why these changes are needed..."
                className="h-20"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleSaveLockedEdit} disabled={saving || !editReason.trim()}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
              <Button variant="outline" onClick={cancelEditLocked}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dispatches')} className="px-2 sm:px-3">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <h1 className="text-base sm:text-xl font-semibold">
              {isEdit ? `Dispatch Note ${dispatchNumber}` : 'New Dispatch Note'}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {isEdit && (
                <span className={`text-xs sm:text-sm px-2 py-0.5 rounded ${
                  status === 'draft' ? 'bg-zinc-100' :
                  status === 'locked' ? 'bg-amber-100 text-amber-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {isEditingLocked ? 'EDITING' : status.toUpperCase()}
                </span>
              )}
              {isEdit && editHistory.length > 0 && (
                <Badge variant="outline" className="text-xs cursor-pointer" onClick={loadEditHistory}>
                  <History className="w-3 h-3 mr-1" />
                  {editHistory.length} edit(s)
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Edit History Button for locked dispatches */}
          {isEdit && status === 'locked' && !isEditingLocked && (
            <Button variant="outline" size="sm" onClick={loadEditHistory} className="sm:h-10">
              <History className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Edit History</span>
            </Button>
          )}
          
          {/* Edit Locked Dispatch Button */}
          {isEdit && status === 'locked' && canEditLockedDispatch && !isEditingLocked && (
            <Button onClick={handleEditLockedClick} variant="secondary" size="sm" className="sm:h-10 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700">
              <Edit className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Edit Locked Dispatch</span>
              <span className="sm:hidden">Edit</span>
            </Button>
          )}
          
          {!isReadOnly && !isEditingLocked && (
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-dispatch" size="sm" className="sm:h-10">
              {saving ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Save className="w-4 h-4 sm:mr-2" />}
              <span className="hidden sm:inline">Save Draft</span>
              <span className="sm:hidden">Save</span>
            </Button>
          )}
          {isEdit && status === 'draft' && (
            <Button onClick={handleLock} variant="secondary" data-testid="btn-lock-dispatch" size="sm" className="sm:h-10">
              <Lock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Lock & Finalize</span>
              <span className="sm:hidden">Lock</span>
            </Button>
          )}
          {isEdit && status !== 'draft' && (
            <Button onClick={handleViewPDF} variant="secondary" data-testid="btn-view-pdf" size="sm" className="sm:h-10">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">View </span>PDF
            </Button>
          )}
          {isEdit && status !== 'draft' && !isEditingLocked && (
            <Button onClick={handleOpenEmailDialog} variant="secondary" data-testid="btn-send-email-top" size="sm" className="sm:h-10">
              <Mail className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Send </span>Email
            </Button>
          )}
          {isEdit && status !== 'draft' && !isEditingLocked && (
            <Button onClick={handlePrintDocument} variant="secondary" data-testid="btn-print-document-top" size="sm" className="sm:h-10">
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Print </span>Doc
            </Button>
          )}
          {isEdit && status !== 'draft' && !isEditingLocked && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="btn-print-pallet-top" size="sm" className="sm:h-10">
                  <Tag className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Pallet </span>Label
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/pallet-labels/${id}`)}>
                  <FileText className="w-4 h-4 mr-2" />
                  Standard (Designer)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openPalletLabels(id, 2, { template: 'agropur' })}>
                  <Package className="w-4 h-4 mr-2" />
                  Agropur
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  setAldiBranchDispatchId(id);
                  setAldiBranchOpen(true);
                }}>
                  <Package className="w-4 h-4 mr-2" />
                  Aldi
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Header Fields */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="py-3 sm:py-4">
            <CardTitle className="text-sm sm:text-base">Dispatch Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs sm:text-sm">Customer *</Label>
                <SearchableSelect
                  options={customers.map(c => ({ value: c.id, label: c.name }))}
                  value={customerId}
                  onValueChange={setCustomerId}
                  placeholder="Select customer..."
                  searchPlaceholder="Type to search customers..."
                  emptyMessage="No customers found"
                  disabled={isReadOnly}
                  data-testid="select-customer"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">PO Number</Label>
                <Input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="12345"
                  disabled={isReadOnly}
                  className="font-mono text-sm"
                  data-testid="input-po-number"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">PO Date</Label>
                <DatePicker
                  value={poDate}
                  onChange={setPoDate}
                  placeholder="Select date"
                  disabled={isReadOnly}
                  data-testid="input-po-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Dispatch/Collection Date</Label>
                <DatePicker
                  value={dispatchDate}
                  onChange={setDispatchDate}
                  placeholder="Select date"
                  disabled={isReadOnly}
                  data-testid="input-dispatch-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Customer Delivery Date</Label>
                <DatePicker
                  value={customerDeliveryDate}
                  onChange={setCustomerDeliveryDate}
                  placeholder="Select date"
                  disabled={isReadOnly}
                  data-testid="input-customer-delivery"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Transport</Label>
                <Select
                  value={transportAgent}
                  onValueChange={setTransportAgent}
                  disabled={isReadOnly}
                >
                  <SelectTrigger data-testid="select-transport">
                    <SelectValue placeholder="Select transport..." />
                  </SelectTrigger>
                  <SelectContent>
                    {transportOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Number of Pallets</Label>
                <Input
                  type="number"
                  min="0"
                  value={numPallets}
                  onChange={(e) => setNumPallets(e.target.value)}
                  placeholder="0"
                  disabled={isReadOnly}
                  className="font-mono text-sm"
                  data-testid="input-pallets"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs sm:text-sm">Prepared By (Operator)</Label>
                <Select
                  value={preparedByOperatorCode || 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') { setPreparedByOperatorCode(''); setPreparedByOperatorName(''); return; }
                    const op = operators.find(o => o.code === v);
                    setPreparedByOperatorCode(v);
                    setPreparedByOperatorName(op?.name || v);
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="h-9" data-testid="select-prepared-by-operator"><SelectValue placeholder="Select operator" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not assigned</SelectItem>
                    {operators.filter(o => o.active !== false).map(op => (
                      <SelectItem key={op.code} value={op.code}>{op.code} — {op.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-4">
            <CardTitle className="text-base">Delivery To</CardTitle>
          </CardHeader>
          <CardContent>
            {customerData ? (
              <div className="space-y-3">
                {/* Address selector if multiple addresses */}
                {customerAddresses.length > 1 && !isReadOnly && (
                  <div className="space-y-1">
                    <Label className="text-xs">Select Delivery Location</Label>
                    <Select value={selectedAddressId} onValueChange={setSelectedAddressId}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select address" />
                      </SelectTrigger>
                      <SelectContent>
                        {customerAddresses.map(addr => (
                          <SelectItem key={addr.id} value={addr.id}>
                            {addr.label} {addr.is_default && '(Default)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Selected address display */}
                <div className="text-sm space-y-1 p-3 bg-muted/50 rounded-lg" data-testid="delivery-address">
                  <p className="font-medium">{customerData.name}</p>
                  {selectedAddress ? (
                    (() => {
                      // Helper to filter out 'nan', 'None', empty strings
                      const clean = (v) => {
                        if (!v) return '';
                        const s = String(v).trim();
                        return s.toLowerCase() === 'nan' || s.toLowerCase() === 'none' ? '' : s;
                      };
                      return (
                      <>
                        {selectedAddress.label !== 'Primary Address' && clean(selectedAddress.label) && (
                          <p className="text-xs text-primary font-medium">{clean(selectedAddress.label)}</p>
                        )}
                        {clean(selectedAddress.address_line1) && <p>{clean(selectedAddress.address_line1)}</p>}
                        {clean(selectedAddress.address_line2) && <p>{clean(selectedAddress.address_line2)}</p>}
                        {[clean(selectedAddress.city), clean(selectedAddress.county), clean(selectedAddress.postcode)]
                          .filter(Boolean).length > 0 && (
                          <p>
                            {[clean(selectedAddress.city), clean(selectedAddress.county), clean(selectedAddress.postcode)]
                              .filter(Boolean).join(', ')}
                          </p>
                        )}
                        {clean(selectedAddress.country) && <p>{clean(selectedAddress.country)}</p>}
                        {clean(selectedAddress.contact_name) && (
                          <p className="pt-2 text-muted-foreground text-xs">
                            Contact: {clean(selectedAddress.contact_name)}
                            {clean(selectedAddress.contact_phone) && ` • ${clean(selectedAddress.contact_phone)}`}
                          </p>
                        )}
                        {clean(selectedAddress.delivery_instructions) && (
                          <p className="pt-1 text-xs text-amber-600">
                            {clean(selectedAddress.delivery_instructions)}
                          </p>
                        )}
                      </>
                      );
                    })()
                  ) : (
                    (() => {
                      const clean = (v) => {
                        if (!v) return '';
                        const s = String(v).trim();
                        return s.toLowerCase() === 'nan' || s.toLowerCase() === 'none' ? '' : s;
                      };
                      return (
                      <>
                        {clean(customerData.delivery_address_line1) && <p>{clean(customerData.delivery_address_line1)}</p>}
                        {clean(customerData.delivery_address_line2) && <p>{clean(customerData.delivery_address_line2)}</p>}
                        {[clean(customerData.delivery_city), clean(customerData.delivery_county), clean(customerData.delivery_postcode)]
                          .filter(Boolean).length > 0 && (
                          <p>
                            {[clean(customerData.delivery_city), clean(customerData.delivery_county), clean(customerData.delivery_postcode)]
                              .filter(Boolean).join(', ')}
                          </p>
                        )}
                        {clean(customerData.delivery_country) && <p>{clean(customerData.delivery_country)}</p>}
                      </>
                      );
                    })()
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Select a customer to see delivery address</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ordered Totals Reference (for information only) */}
      {orderedTotals.units > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Package className="w-4 h-4" />
            <span className="font-medium">Order Reference:</span>
          </div>
          <div className="flex gap-6 text-sm">
            <div className="text-blue-700">
              <span className="text-muted-foreground">Ordered Units:</span>
              <span className="font-semibold ml-1">{orderedTotals.units}</span>
            </div>
            <div className="text-purple-700">
              <span className="text-muted-foreground">Ordered Cases:</span>
              <span className="font-semibold ml-1">{orderedTotals.cases.toFixed(1)}</span>
            </div>
            <div className="text-blue-700">
              <span className="text-muted-foreground">Ordered Weight:</span>
              <span className="font-semibold ml-1">{orderedTotals.weight.toFixed(2)} kg</span>
            </div>
          </div>
        </div>
      )}

      {/* Lines Grid */}
      <Card>
        <CardHeader className="py-4 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Line Items</CardTitle>
          {!isReadOnly && (
            <Button size="sm" variant="outline" onClick={addLine} data-testid="btn-add-line">
              <Plus className="w-4 h-4 mr-1" />
              Add Row
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="data-grid w-full table-fixed min-w-[900px]">
            <thead>
              <tr>
                <th className="w-[180px] max-w-[180px]">Product *</th>
                <th className="w-[100px] max-w-[100px]">Brand/Pkg</th>
                <th className="w-[130px] max-w-[130px]">Batch *</th>
                <th className="w-[85px] max-w-[85px]">Use By *</th>
                <th className="w-[70px] max-w-[70px]">Units *</th>
                <th className="w-[75px] max-w-[75px]">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-1 cursor-help">
                          Wt (kg) *
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Enter manually or leave blank for auto-calculation</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th className="w-[65px] max-w-[65px]">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-1 cursor-help">
                          Cases
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Enter manually or leave blank for auto-calculation</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th className="w-[90px] max-w-[90px]">Notes</th>
                {!isReadOnly && <th className="w-[45px] max-w-[45px]"></th>}
                {isReadOnly && <th className="w-[45px] max-w-[45px]">Print</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.id} data-testid={`dispatch-line-${index}`} className={line.out_of_stock ? 'bg-red-50/70' : ''}>
                  <td className="editable-cell overflow-hidden">
                    {line.out_of_stock ? (
                      <div className="flex items-center gap-1 px-2 h-9">
                        <span className="text-sm truncate">{line.product_name || 'Product'}</span>
                        <span className="px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold rounded tracking-wider shrink-0">OUT OF STOCK</span>
                      </div>
                    ) : (
                    <SearchableSelect
                      options={products.map(p => ({ value: p.id, label: p.name }))}
                      value={line.product_id}
                      onValueChange={(v) => updateLine(index, 'product_id', v)}
                      placeholder="Select..."
                      searchPlaceholder="Search products..."
                      emptyMessage="No products found"
                      disabled={isReadOnly}
                      fallbackLabel={line.product_name}
                      triggerClassName="border-0 bg-transparent rounded-none h-9 focus:ring-2 focus:ring-primary w-full truncate"
                    />
                    )}
                  </td>
                  <td className="editable-cell overflow-hidden">
                    {/* Brand/Packaging dropdown */}
                    <Select
                      value={line.cobranding_id || ''}
                      onValueChange={(v) => updateLine(index, 'cobranding_id', v === 'none' ? '' : v)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="border-0 bg-transparent rounded-none h-9 text-xs">
                        <SelectValue placeholder="Standard" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Standard</SelectItem>
                        {cobrandings.map((cb) => (
                          <SelectItem key={cb.id} value={cb.id}>{cb.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="editable-cell overflow-hidden">
                    {/* Batch Code Entry - Free text with auto-generate */}
                    {line.product_id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="text"
                          value={line.batch_code || ''}
                          onChange={(e) => updateLine(index, 'batch_code', e.target.value.toUpperCase())}
                          onKeyDown={(e) => handleKeyDown(e, index, 'batch_code')}
                          placeholder="Enter batch..."
                          disabled={isReadOnly}
                          className="border-0 bg-transparent rounded-none h-9 text-xs font-mono flex-1 min-w-0"
                          data-testid={`batch-code-input-${index}`}
                          ref={(el) => { batchCodeRefs.current[index] = el; }}
                        />
                        {!isReadOnly && !line.batch_code && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-primary hover:bg-primary/10"
                            onClick={() => {
                              // Auto-generate batch code: YYMMDD-SEQ
                              const now = new Date();
                              const yy = String(now.getFullYear()).slice(-2);
                              const mm = String(now.getMonth() + 1).padStart(2, '0');
                              const dd = String(now.getDate()).padStart(2, '0');
                              const seq = String(index + 1).padStart(2, '0');
                              const generatedCode = `${yy}${mm}${dd}-${seq}`;
                              updateLine(index, 'batch_code', generatedCode);
                            }}
                            title="Auto-generate batch code"
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            Gen
                          </Button>
                        )}
                        {!isReadOnly && line.batch_code && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => {
                              updateLine(index, 'batch_code', '');
                              updateLine(index, 'batch_id', '');
                            }}
                            title="Clear batch"
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground px-2">Select product first</span>
                    )}
                  </td>
                  <td className="editable-cell overflow-hidden">
                    {/* Use By Date - Calendar picker with No Date option */}
                    <div className="flex items-center gap-1">
                      {line.no_use_by_date ? (
                        <span className="text-xs font-medium text-amber-700 px-2 py-1.5 flex-1">No Date</span>
                      ) : (
                        <DatePicker
                          value={line.use_by_date || ''}
                          onChange={(formattedDate) => {
                            updateLine(index, 'use_by_date', formattedDate);
                          }}
                          disabled={isReadOnly || !!line.batch_id}
                          className="border-0 bg-transparent rounded-none h-9 text-xs flex-1"
                          placeholder={line.batch_id ? "Auto-filled" : "Select date"}
                          defaultMonth={getLinePrediction(line)?.predicted_date}
                          data-testid={`use-by-date-${index}`}
                        />
                      )}
                      {!line.no_use_by_date && !line.batch_id && line.use_by_date && getLinePrediction(line)?.predicted_date === line.use_by_date && (
                        <span
                          className="shrink-0 cursor-help"
                          title={getUseByPredictionTitle(getLinePrediction(line))}
                          data-testid={`use-by-auto-indicator-${index}`}
                        >
                          <Sparkles className="w-3 h-3 text-violet-500" />
                        </span>
                      )}
                      {!isReadOnly && !line.batch_id && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center pr-1">
                                <Checkbox
                                  checked={line.no_use_by_date || false}
                                  onCheckedChange={(checked) => updateLine(index, 'no_use_by_date', checked)}
                                  className="h-3.5 w-3.5"
                                  data-testid={`no-date-checkbox-${index}`}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">No Use By Date</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </td>
                  <td className="editable-cell">
                    {/* Quantity with batch limit enforcement */}
                    {(() => {
                      const maxQty = getMaxQuantityForLine(line);
                      return (
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            max={maxQty || undefined}
                            value={line.quantity}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Enforce max quantity if batch is selected
                              if (maxQty && parseFloat(value) > maxQty) {
                                toast.error(`Maximum available: ${maxQty} units`);
                                value = maxQty.toString();
                              }
                              updateLine(index, 'quantity', value);
                            }}
                            onKeyDown={(e) => handleKeyDown(e, index, 'quantity')}
                            disabled={isReadOnly}
                            className="data-grid-input font-mono text-right text-sm pr-8"
                            placeholder="0"
                            data-testid={`input-quantity-${index}`}
                          />
                          {maxQty && (
                            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                              /{maxQty}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="editable-cell">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.weight_override !== '' ? line.weight_override : ''}
                      onChange={(e) => updateLine(index, 'weight_override', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, 'weight')}
                      disabled={isReadOnly}
                      className={`data-grid-input font-mono text-right text-sm ${
                        line.weight_override === '' || line.weight_override === undefined 
                          ? 'text-muted-foreground' 
                          : ''
                      }`}
                      placeholder={getSuggestedValues(line, products).suggested_weight.toString() || '0'}
                      data-testid={`input-weight-${index}`}
                      title={line.weight_override !== '' && line.weight_override !== undefined 
                        ? 'Manual entry' 
                        : `Suggested: ${getSuggestedValues(line, products).suggested_weight} kg`}
                    />
                  </td>
                  <td className="editable-cell">
                    <Input
                      type="number"
                      min="0"
                      value={line.cases_override !== '' ? line.cases_override : ''}
                      onChange={(e) => updateLine(index, 'cases_override', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, 'cases')}
                      disabled={isReadOnly}
                      className={`data-grid-input font-mono text-right text-sm ${
                        line.cases_override === '' || line.cases_override === undefined 
                          ? 'text-muted-foreground' 
                          : ''
                      }`}
                      placeholder={getSuggestedValues(line, products).suggested_cases.toString() || '0'}
                      data-testid={`input-cases-${index}`}
                      title={line.cases_override !== '' && line.cases_override !== undefined 
                        ? 'Manual entry' 
                        : `Suggested: ${getSuggestedValues(line, products).suggested_cases}`}
                    />
                  </td>
                  <td className="editable-cell overflow-hidden">
                    <Input
                      value={line.notes || ''}
                      onChange={(e) => updateLine(index, 'notes', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, 'notes')}
                      disabled={isReadOnly}
                      className="data-grid-input text-xs truncate"
                      placeholder="Notes..."
                    />
                  </td>
                  {!isReadOnly && (
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeLine(index)}
                        disabled={lines.length === 1}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  )}
                  {isReadOnly && (
                    <td className="text-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePrintLabel(index)}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                              data-testid={`print-label-${index}`}
                            >
                              <Printer className="w-3 h-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Print label for this line</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Totals */}
      <div className="flex justify-end">
        <Card className="w-full max-w-xl">
          <CardContent className="p-4">
            <div className="space-y-3">
              {/* Product-wise breakdown */}
              {calculatedTotals.byProduct && calculatedTotals.byProduct.length > 0 && (
                <div className="space-y-2 pb-3 border-b">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    By Product
                  </div>
                  {calculatedTotals.byProduct.map((product, idx) => (
                    <div key={product.product_id || idx} className="grid grid-cols-4 gap-2 text-sm bg-muted/30 p-2 rounded">
                      <div className="col-span-4 font-medium text-primary truncate" title={product.product_name}>
                        {product.product_name} <span className="text-muted-foreground font-normal">({product.lines} line{product.lines > 1 ? 's' : ''})</span>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground block">Cases</span>
                        <span className="font-mono">{formatNumber(product.cases)}</span>
                      </div>
                      <div>
                        <span className="text-xs text-blue-600 block font-medium">Net Wt</span>
                        <span className="font-mono font-bold text-blue-800">{formatNumber(product.netWeight)} kg</span>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground block">Gross</span>
                        <span className="font-mono text-xs text-muted-foreground">{formatNumber(product.grossWeight)} kg</span>
                      </div>
                      <div></div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Combined totals */}
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Combined Totals
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  Total Cases
                </span>
                <span className="font-mono font-medium" data-testid="total-cases">
                  {formatNumber(isEdit && status !== 'draft' ? totals.cases : calculatedTotals.cases)}
                </span>
              </div>
              
              {/* NET WEIGHT - Primary (what customer pays for) */}
              <div className="flex justify-between items-center text-sm bg-blue-50 border border-blue-200 rounded-lg p-3 -mx-2">
                <span className="text-blue-800 font-semibold flex items-center gap-1">
                  📦 Net Weight (Ordered)
                </span>
                <span className="font-mono font-bold text-xl text-blue-900" data-testid="total-net-weight">
                  {formatNumber(isEdit && status !== 'draft' ? totals.netWeight : calculatedTotals.netWeight)} kg
                </span>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  Packaging Weight
                </span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={packagingWeight}
                    onChange={(e) => setPackagingWeight(e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Auto (2%)"
                    className="w-24 h-7 text-sm font-mono text-right"
                    data-testid="input-packaging-weight"
                  />
                  <span className="text-xs text-muted-foreground">kg</span>
                </div>
              </div>
              
              {/* GROSS WEIGHT - Secondary (for transport) */}
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="text-muted-foreground flex items-center gap-1 text-xs">
                  Gross Weight (incl. packaging)
                </span>
                <span className="font-mono text-muted-foreground" data-testid="total-gross-weight">
                  {formatNumber(
                    packagingWeight 
                      ? (isEdit && status !== 'draft' ? totals.netWeight : calculatedTotals.netWeight) + parseFloat(packagingWeight)
                      : (isEdit && status !== 'draft' ? totals.grossWeight : calculatedTotals.grossWeight)
                  )} kg
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base">Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isReadOnly}
            className="w-full h-24 px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Additional notes for this dispatch..."
            data-testid="input-notes"
          />
        </CardContent>
      </Card>

      {/* Bottom Action Buttons */}
      <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={() => navigate('/dispatches')} data-testid="btn-exit-dispatch">
          Exit Without Saving
        </Button>
        {!isReadOnly && (
          <>
            <Button onClick={handleSave} disabled={saving} variant="secondary" data-testid="btn-save-dispatch-bottom">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
            {isEdit && status === 'draft' && (
              <Button onClick={handleLock} data-testid="btn-lock-dispatch-bottom">
                <Lock className="w-4 h-4 mr-2" />
                Lock & Finalize
              </Button>
            )}
          </>
        )}
      </div>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Dispatch Note via Email
            </DialogTitle>
            <DialogDescription>
              Send dispatch note #{dispatchNumber} to the customer with PDF attachment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>To *</Label>
              <Input
                type="email"
                value={emailData.to_email}
                onChange={(e) => setEmailData({ ...emailData, to_email: e.target.value })}
                placeholder="customer@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label>CC (optional)</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  placeholder="Add CC email..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCc())}
                />
                <Button type="button" variant="outline" onClick={handleAddCc}>
                  Add
                </Button>
              </div>
              {emailData.cc_emails.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {emailData.cc_emails.map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveCc(email)}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Dispatch Note..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                placeholder="Enter your message..."
                className="h-32"
              />
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <input
                type="checkbox"
                id="attach-pdf"
                checked={emailData.attach_pdf}
                onChange={(e) => setEmailData({ ...emailData, attach_pdf: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="attach-pdf" className="text-sm flex items-center gap-2 cursor-pointer">
                <FileText className="w-4 h-4 text-muted-foreground" />
                Attach Dispatch Note PDF
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailData.to_email || !emailData.subject}>
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Email</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Locked Dispatch Dialog */}
      <Dialog open={editLockedDialogOpen} onOpenChange={setEditLockedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Edit Locked Dispatch
            </DialogTitle>
            <DialogDescription>
              This dispatch has been locked. Editing it requires a reason which will be tracked for audit purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for editing *</Label>
              <Textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="E.g., Customer requested quantity change, Incorrect batch number..."
                className="h-24"
              />
              <p className="text-xs text-muted-foreground">
                This reason will be recorded in the dispatch&apos;s edit history.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLockedDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmEditLocked} disabled={!editReason.trim()}>
              <Edit className="w-4 h-4 mr-2" />
              Continue to Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Edit History - Dispatch #{dispatchNumber}
            </DialogTitle>
            <DialogDescription>
              Record of all changes made to this dispatch after it was locked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editHistory.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No edit history found. This dispatch has not been modified since being locked.
              </p>
            ) : (
              <div className="space-y-4">
                {editHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-muted/30">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-medium">{entry.user_email}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        Edit #{editHistory.length - idx}
                      </Badge>
                    </div>
                    <div className="mt-3">
                      <div className="text-sm font-medium">Reason:</div>
                      <p className="text-sm bg-white p-2 rounded border mt-1">
                        {entry.reason}
                      </p>
                    </div>
                    {entry.changes && entry.changes.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm font-medium">Changes made:</div>
                        <ul className="list-disc list-inside text-sm mt-1 space-y-1">
                          {entry.changes.map((change, cIdx) => (
                            <li key={cIdx} className="text-muted-foreground">{change}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use-By Date Warning Dialog */}
      <Dialog open={useByWarningOpen} onOpenChange={setUseByWarningOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Expired Use-By Date Warning
            </DialogTitle>
            <DialogDescription>
              The following batches have use-by dates in the past. Are you sure you want to proceed with this dispatch?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              {expiredBatches.map((batch, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <div>
                    <span className="font-medium">{batch.product_name}</span>
                    {batch.batch_code && (
                      <span className="text-muted-foreground ml-2 font-mono text-xs">({batch.batch_code})</span>
                    )}
                  </div>
                  <span className="text-red-600 font-medium">{batch.use_by_date}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              <strong>Note:</strong> Dispatching products past their use-by date may violate food safety regulations.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUseByWarningOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={proceedWithLock} data-testid="btn-confirm-expired">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Proceed Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AldiBranchDialog
        open={aldiBranchOpen}
        onClose={() => setAldiBranchOpen(false)}
        dispatchId={aldiBranchDispatchId}
      />
    </div>
  );
}